import { UntilDestroy } from '@ngneat/until-destroy';
import { inject, Inject, Injectable } from '@angular/core';
import { DataService } from 'ag-common-svc/lib/services/data.service';
import { Policy, PolicyKeys, PolicyStatus, PolicyTypes, PolicyVersionMap } from '@ag-common-lib/public-api';
import { FIREBASE_APP } from 'ag-common-svc/lib/injections/firebase-app';
import { FirebaseApp } from 'firebase/app';
import { policiesFromFirestore, updateWithAgentMetadata } from './user-agreements-service-utils';
import { catchError, combineLatest, Observable, of, shareReplay } from 'rxjs';
import { DataSource } from 'devextreme/common/data';
import { map, switchMap } from 'rxjs/operators';
import ArrayStore from 'devextreme/data/array_store';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { QueryParam, WhereFilterOperandKeys } from 'ag-common-svc/lib/dao/CommonFireStoreDao.dao';
import { toSignal } from '@angular/core/rxjs-interop';
import { AgentSearchService } from 'ag-common-svc/lib/services/agent-search';

export const POLICY_FIRESTORE_COLLECTION = 'policy';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class PolicyService extends DataService<Policy> {
  private agentSearchService = inject(AgentSearchService);
  private readonly policyCollectionPath = POLICY_FIRESTORE_COLLECTION;

  private readonly _policies$: Observable<Policy[]>;

  // to cache results
  private _currentPublishedByTypeCache = new Map<PolicyTypes, Observable<Policy[]>>();
  private _lastPublishedDocumentsCached$: Observable<Policy[]>;

  masterVersionsDataSource$: Observable<DataSource>;

  publishedMasterPrivacyPolicySig = toSignal(this.getCurrentPublishedDocumentByType(PolicyTypes.privacyPolicy));

  publishedMasterTermsOfServiceSig = toSignal(this.getCurrentPublishedDocumentByType(PolicyTypes.termsOfService));

  constructor(
    @Inject(FIREBASE_APP) fireBaseApp: FirebaseApp,
  ) {
    super(fireBaseApp, PolicyService.fromFirestore, null);
    super.setCollection(this.policyCollectionPath);

    this._policies$ = this.getList([], { sortField: PolicyKeys.version }).pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.masterVersionsDataSource$ = this._policies$.pipe(
      switchMap(policies => {
        if (!policies?.length) {
          return of(
            new DataSource({
              store: new ArrayStore({
                key: BaseModelKeys.dbId,
                data: [],
              }),
            }),
          );
        }

        return updateWithAgentMetadata(policies, this.agentSearchService).pipe(
          map(
            data =>
              new DataSource({
                store: new ArrayStore({
                  key: BaseModelKeys.dbId,
                  data: data,
                }),
              }),
          ),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this._lastPublishedDocumentsCached$ = this.fetchLastPublishedDocuments().pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  static readonly fromFirestore = policiesFromFirestore;

  getCurrentPublishedDocumentByType(type: PolicyTypes): Observable<Policy[]> {
    if (!this._currentPublishedByTypeCache.has(type)) {
      const qp: QueryParam[] = [
        new QueryParam(PolicyKeys.type, WhereFilterOperandKeys.equal, type),
        new QueryParam(PolicyKeys.status, WhereFilterOperandKeys.equal, PolicyStatus.published),
      ];
      const policies$ = this.fsDao
        .getList(this.policyCollectionPath, qp, null, false)
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));
      this._currentPublishedByTypeCache.set(type, policies$);
    }
    return this._currentPublishedByTypeCache.get(type);
  }

  getLastPublishedDocuments(): Observable<Policy[]> {
    return this._lastPublishedDocumentsCached$;
  }

  /**
   * Get Policy versions as {[version]:silent}[] from policy collection by type
   * ordered by updated_date for published and archived status
   * @param policyType The policy type to filter by
   * @returns Observable<PolicyVersionMap[]> Array of objects with version as key and silent as value
   */
  getPolicyVersionsByType(policyType: PolicyTypes): Observable<PolicyVersionMap[]> {
    // Fallback implementation: Fetch published and archived separately and merge
    const qpPublished: QueryParam[] = [
      new QueryParam(PolicyKeys.type, WhereFilterOperandKeys.equal, policyType),
      new QueryParam(PolicyKeys.status, WhereFilterOperandKeys.equal, PolicyStatus.published),
    ];
    const qpArchived: QueryParam[] = [
      new QueryParam(PolicyKeys.type, WhereFilterOperandKeys.equal, policyType),
      new QueryParam(PolicyKeys.status, WhereFilterOperandKeys.equal, PolicyStatus.archived),
    ];
    const published$ = this.getList(qpPublished, { sortField: BaseModelKeys.updatedDate, order: 'desc' }).pipe(catchError(() => of([])));
    const archived$ = this.getList(qpArchived, { sortField: BaseModelKeys.updatedDate, order: 'desc' }).pipe(catchError(() => of([])));

    return combineLatest([published$, archived$]).pipe(
      map((results: [Policy[], Policy[]]) => {
        const mergedPolicies = [...(results[0] || []), ...(results[1] || [])];
        return mergedPolicies.map(policy => {
          const version = policy[PolicyKeys.version];
          const silent = policy[PolicyKeys.silent];
          return { [version]: silent } as PolicyVersionMap;
        });
      }),
      catchError(error => {
        console.error('Error getting policy versions by type', error);
        return of([]);
      }),
    );
  }

  private fetchLastPublishedDocuments(): Observable<Policy[]> {
    const qp: QueryParam[] = [new QueryParam(PolicyKeys.status, WhereFilterOperandKeys.equal, PolicyStatus.published)];
    return this.fsDao.getList(this.policyCollectionPath, qp, null, false);
  }
}
